import { Accessor, children, Component, createContext, createEffect, createMemo, createRoot, JSXElement, Match, onMount, ParentProps, Show, Signal, Switch, useContext } from "solid-js"
import { createProxySignal, requireContext, uuidv4 } from "common";
import styles from './st-radio-group.module.css'

export type RadioButtonType = "default" | "button" | 'button-group';

export class STRadioGroupProps<T extends string> {
    type?: RadioButtonType = "default";
    name?: string;
    value!: [Accessor<T>, (value: T) => void]
    vertical?: boolean;
}

export class RadioGroupContext {
    name: string;
    type: RadioButtonType;
    value: Signal<string>;

    constructor(name: string, type: RadioButtonType, value: Signal<string>) {
        this.name = name;
        this.type = type;
        this.value = value;
    }
}

const RadioGroupContextJSX = createContext<RadioGroupContext>();
export function requireRadioGroupContext() { return requireContext(RadioGroupContextJSX); }

export default function RadioGroup<T extends string>(props: ParentProps<STRadioGroupProps<T>>) {
    const flexboxClasses = createMemo(() => props.vertical ? 'flexbox vertical middle' : 'flexbox horizontal middle');

    function Inner(props: ParentProps<STRadioGroupProps<T>>) {
        return <fieldset class={`${styles.radioGroup} ${flexboxClasses()}`} data-type={props.type} style={{ "align-items": "start" }}>
            {props.children}
        </fieldset>
    }

    const value = createProxySignal(
        () => props.value[0]().toString(),
        (v) => props.value[1](v as T)
    );

    return <RadioGroupContextJSX.Provider value={new RadioGroupContext(props.name ?? uuidv4(), props.type ?? 'default', value)}>
        <Inner {...props} />
    </RadioGroupContextJSX.Provider>
}

RadioGroup.defaultProps = new STRadioGroupProps<string>();

export class STRadioOptionProps<T extends string> {
    icon?: string;
    label?: string;
    value?: T;
}

RadioGroup.Option = function <T extends string>(props: ParentProps<STRadioOptionProps<T>>) {
    const ctx = requireRadioGroupContext();
    const value = props.value || props.label;
    const flexClasses = createMemo(() => !props.icon ? 'auto flexbox horizontal middle' : 'auto flexbox vertical middle');

    function init(input: HTMLInputElement) {
        input.setAttribute('name', ctx.name);

        const updateInputState = () => {
            if (input.checked) {
                const nodes = input.closest(`.${styles.radioGroup}`)!.querySelectorAll(`.${styles.checked}`);
                Array.from(nodes ?? []).forEach(n => n.classList.remove(styles.checked));

                input.parentElement?.classList.add(styles.checked);
                ctx.value[1](input.value);
            }
            else {
                input.parentElement?.classList.remove(styles.checked);
            }
        };

        createEffect(() => {
            input.checked = input.value === ctx.value[0]();
            updateInputState();
        });

        input.addEventListener('change', (ev: Event) => updateInputState());
    }

    return <>
        <label class={`${styles.radio} ${flexClasses()}`}>
            <input ref={init} type="radio" value={value} />
            <Switch>
                <Match when={props.icon?.includes('/')}>
                    <img class={styles.icon} alt={value} src={window.ST.TransformImagePath?.call(null, props.icon) || props.icon} />
                </Match>
                <Match when={props.icon}>
                    <i class={`${styles.icon} ${props.icon}`} role="presentation" />
                </Match>
            </Switch>
            <Show when={props.label} fallback={props.children}>
                <span>{props.label}</span>
            </Show>
        </label>
    </>
}
